import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHeader, TableRow, TableHead } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader } from '../../components/ui/loader';

// icons
import { Add, Edit, Trash, Shield, UserSquare, SearchNormal, Grid3 } from 'iconsax-react';

// services
import roleService from '../../services/roleService';

// utils
import { getPermissionErrorMessage } from '../../utils/permissionMessages';
import { cn } from '../../lib/utils';

// Validation schema
const roleValidationSchema = Yup.object({
  name: Yup.string()
    .required('Role name is required')
    .min(2, 'Role name must be at least 2 characters')
    .max(50, 'Role name must not exceed 50 characters')
    .matches(/^[A-Z_]+$/, 'Role name must be uppercase letters and underscores only (e.g., CUSTOM_ADMIN)'),
  displayName: Yup.string()
    .required('Display name is required')
    .min(2, 'Display name must be at least 2 characters')
    .max(100, 'Display name must not exceed 100 characters'),
  description: Yup.string()
    .max(500, 'Description must not exceed 500 characters'),
  permissions: Yup.array()
    .min(1, 'At least one permission must be selected')
    .required('Permissions are required'),
  roleType: Yup.string()
    .oneOf(['ADMIN', 'CLIENT'], 'Invalid role type')
    .required('Role type is required')
});

const RoleManagement = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissionMatrix, setPermissionMatrix] = useState({ permissions: [], roles: [] });
  const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 0, currentPage: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('create');
  const [selectedRole, setSelectedRole] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const getInitialFormValues = () => {
    console.log('Getting initial form values for role:', selectedRole);
    return {
      name: selectedRole?.name || '',
      displayName: selectedRole?.displayName || '',
      description: selectedRole?.description || '',
      permissions: selectedRole?.permissions || [],
      roleType: selectedRole?.roleType || 'ADMIN'
    };
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, typeFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Prepare pagination params
      const params = {
        page: page + 1, // Backend expects 1-based page
        limit: rowsPerPage,
        search: searchTerm,
        type: typeFilter
      };
      
      // Fetch roles and permission matrix (required for role management)
      const [rolesResult, matrixResult] = await Promise.all([
        roleService.getAllRoles(params),
        roleService.getPermissionMatrix()
      ]);

      if (rolesResult.success) {
        setRoles(rolesResult.data.roles || []);
        setPagination(rolesResult.data.pagination || { totalItems: 0, totalPages: 0, currentPage: 1 });
      }
      if (matrixResult.success) {
        console.log('Permission matrix received:', matrixResult.data);
        setPermissionMatrix(matrixResult.data);
      }
      
      // Try to fetch users (optional - for stats only)
      try {
        const usersResult = await roleService.getAdminUsers();
        if (usersResult.success) {
          console.log('Users data received:', usersResult.data);
          setUsers(usersResult.data.users || []);
        }
      } catch (userError) {
        console.log('User data not available (insufficient permissions)');
        // Don't set error for user fetch failure - it's optional
      }
      
    } catch (error) {
      console.error('Error fetching role data:', error);
      
      // Check if it's a permission error
      if (error.response?.data?.error?.code === 'INSUFFICIENT_PERMISSIONS') {
        const friendlyMessage = getPermissionErrorMessage(error.response.data.error.message);
        setError(friendlyMessage);
      } else {
        setError('Failed to load role data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    setDialogType('create');
    setSelectedRole(null);
    setSubmitError('');
    setSubmitSuccess('');
    setOpenDialog(true);
  };

  const handleEditRole = (role) => {
    setDialogType('edit');
    setSelectedRole(role);
    setSubmitError('');
    setSubmitSuccess('');
    setOpenDialog(true);
  };

  const handleDeleteRole = (role) => {
    setDialogType('delete');
    setSelectedRole(role);
    setSubmitError('');
    setSubmitSuccess('');
    setOpenDialog(true);
  };

  const handleViewRole = (role) => {
    console.log('Viewing role:', role);
    console.log('Current users:', users);
    console.log('Users with this role:', Array.isArray(users) ? users.filter(user => user.role === role.name) : []);
    setDialogType('view');
    setSelectedRole(role);
    setSubmitError('');
    setSubmitSuccess('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRole(null);
    setSubmitError('');
    setSubmitSuccess('');
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      setSubmitError('');
      setSubmitSuccess('');
      
      if (dialogType === 'create') {
        const result = await roleService.createRole(values);
        if (result.success) {
          setSubmitSuccess('Role created successfully!');
          await fetchData();
          setTimeout(() => handleCloseDialog(), 1500);
        } else {
          setSubmitError(result.message || 'Failed to create role');
        }
      } else if (dialogType === 'edit') {
        console.log('Updating role with values:', {
          displayName: values.displayName,
          description: values.description,
          permissions: values.permissions
        });
        
        const result = await roleService.updateRole(selectedRole._id, {
          displayName: values.displayName,
          description: values.description,
          permissions: values.permissions
        });
        
        console.log('Update result:', result);
        
        if (result.success) {
          setSubmitSuccess('Role updated successfully!');
          await fetchData(); // Refresh the data
          setTimeout(() => handleCloseDialog(), 1500);
        } else {
          setSubmitError(result.message || 'Failed to update role');
        }
      }
    } catch (error) {
      console.error(`Error ${dialogType} role:`, error);
      setSubmitError(error.response?.data?.message || `Failed to ${dialogType} role. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setSubmitError('');
      
      const result = await roleService.deleteRole(selectedRole._id);
      if (result.success) {
        setSubmitSuccess('Role deleted successfully!');
        await fetchData();
        setTimeout(() => handleCloseDialog(), 1500);
      } else {
        setSubmitError(result.message || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      setSubmitError(error.response?.data?.message || 'Failed to delete role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleStats = () => {
    const totalRoles = pagination.totalItems || 0;
    const systemRoles = roles.filter(role => role.isSystem).length;
    const customRoles = roles.filter(role => !role.isSystem).length;
    const totalUsers = Array.isArray(users) ? users.length : 0;

    return { totalRoles, systemRoles, customRoles, totalUsers };
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value === 'all' ? '' : value);
    setPage(0); // Reset to first page when filtering
  };

  const stats = getRoleStats();



  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Role Management
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage user roles and permissions for the admin platform
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-primary">
                  {loading ? '...' : stats.totalRoles}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Total Roles
                </p>
              </div>
              <div className="p-2 bg-primary/10 rounded-lg">
                <Shield size={24} className="text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">
                  {loading ? '...' : stats.systemRoles}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  System Roles
                </p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Shield size={24} className="text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-amber-600">
                  {loading ? '...' : stats.customRoles}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Custom Roles
                </p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Shield size={24} className="text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-sky-600">
                  {loading ? '...' : stats.totalUsers}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Total Users
                </p>
              </div>
              <div className="p-2 bg-sky-100 rounded-lg">
                <UserSquare size={24} className="text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-1/3 relative">
              <SearchNormal className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Search roles..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-9 w-full bg-background"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto items-center">
              <div className="w-full sm:w-48">
                <Select value={typeFilter || "all"} onValueChange={handleTypeFilterChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="system">System Roles</SelectItem>
                    <SelectItem value="custom">Custom Roles</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={() => navigate('/roles/permissions')}
                  className="flex items-center gap-1.5 w-full sm:w-auto"
                >
                  <Grid3 size={18} />
                  Permission Matrix
                </Button>
                <Button
                  onClick={handleCreateRole}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5 w-full sm:w-auto shrink-0"
                >
                  <Add size={18} />
                  Create Role
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Roles Table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-foreground">
              Roles & Permissions
            </h2>
          </div>
          {loading ? (
            <Loader message="Loading platform roles..." className="min-h-[300px]" />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center font-semibold">Role Name</TableHead>
                      <TableHead className="text-center font-semibold">Display Name</TableHead>
                      <TableHead className="text-center font-semibold">Type</TableHead>
                      <TableHead className="text-center font-semibold">Permissions</TableHead>
                      <TableHead className="text-center font-semibold">Created By</TableHead>
                      <TableHead className="text-center font-semibold pr-6">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No roles found
                        </TableCell>
                      </TableRow>
                    ) : (
                      roles.map((role) => (
                        <TableRow key={role._id} className="hover:bg-muted/50 transition-colors">
                          <TableCell className="text-center">
                            <span 
                              onClick={() => handleViewRole(role)}
                              className="font-bold text-primary hover:underline cursor-pointer"
                            >
                              {role.name}
                            </span>
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">{role.displayName}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col gap-1 items-center justify-center">
                              <Badge className={cn("font-medium text-[11px] px-2 py-0.5 border-none", 
                                role.isSystem ? "bg-primary/10 text-primary hover:bg-primary/10" : "bg-slate-100 text-slate-700 hover:bg-slate-100"
                              )}>
                                {role.isSystem ? 'System' : 'Custom'}
                              </Badge>
                              <Badge variant="outline" className="text-[10px] text-muted-foreground border-slate-200">
                                {role.roleType === 'CLIENT' ? 'Client' : 'Admin'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            {role.permissions?.length || 0} permissions
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            {role.createdBy?.name || 'System'}
                          </TableCell>
                          <TableCell className="text-center pr-6">
                            <div className="flex items-center justify-center gap-1.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditRole(role)}
                                disabled={role.isSystem}
                                className="h-8 w-8 text-muted-foreground hover:text-foreground disabled:opacity-50"
                              >
                                <Edit size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteRole(role)}
                                disabled={role.isSystem}
                                className="h-8 w-8 text-destructive hover:bg-destructive/10 disabled:opacity-50"
                              >
                                <Trash size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Custom Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t gap-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">Rows per page:</span>
                  <Select
                    value={String(rowsPerPage)}
                    onValueChange={(val) => {
                      setRowsPerPage(Number(val));
                      setPage(0);
                    }}
                  >
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue placeholder={String(rowsPerPage)} />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25].map((size) => (
                        <SelectItem key={size} value={String(size)}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    Showing {pagination.totalItems > 0 ? page * rowsPerPage + 1 : 0}–{Math.min((page + 1) * rowsPerPage, pagination.totalItems)} of {pagination.totalItems}
                  </span>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={(page + 1) * rowsPerPage >= pagination.totalItems}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Role Dialog */}
      <Dialog open={openDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'create' && 'Create New Role'}
              {dialogType === 'edit' && 'Edit Role'}
              {dialogType === 'delete' && 'Delete Role'}
              {dialogType === 'view' && 'Role Details'}
            </DialogTitle>
            <DialogDescription>
              {dialogType === 'create' && 'Provide credentials and allocate permissions to create a new role.'}
              {dialogType === 'edit' && 'Modify details and permissions for this role.'}
              {dialogType === 'delete' && 'Confirm deletion of this custom role.'}
              {dialogType === 'view' && 'View administrative details and allocated permissions for this role.'}
            </DialogDescription>
          </DialogHeader>

          {submitError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
          {submitSuccess && (
            <Alert className="bg-emerald-50 text-emerald-700 border-emerald-200 mb-4">
              <AlertDescription>{submitSuccess}</AlertDescription>
            </Alert>
          )}
          
          {dialogType === 'view' ? (
            <div className="space-y-6 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3.5">
                  <h4 className="text-sm font-semibold text-foreground border-b pb-1">Role Information</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground"><strong className="text-foreground font-medium">Name:</strong> {selectedRole?.name}</p>
                    <p className="text-muted-foreground"><strong className="text-foreground font-medium">Display Name:</strong> {selectedRole?.displayName}</p>
                    <p className="text-muted-foreground"><strong className="text-foreground font-medium">Type:</strong> {selectedRole?.isSystem ? 'System Role' : 'Custom Role'}</p>
                    <p className="text-muted-foreground"><strong className="text-foreground font-medium">Scope:</strong> {selectedRole?.roleType === 'CLIENT' ? 'Client / Agency Portal' : 'Admin Platform'}</p>
                    <p className="text-muted-foreground"><strong className="text-foreground font-medium">Description:</strong> {selectedRole?.description || 'No description'}</p>
                    <p className="text-muted-foreground"><strong className="text-foreground font-medium">User Count:</strong> {Array.isArray(users) ? users.filter(user => user.role === selectedRole?.name).length : 0} users</p>
                  </div>
                </div>
                
                <div className="space-y-3.5">
                  <h4 className="text-sm font-semibold text-foreground border-b pb-1">Permissions ({selectedRole?.permissions?.length || 0})</h4>
                  <div className="flex flex-wrap gap-1.5 max-h-[220px] overflow-y-auto pr-1">
                    {selectedRole?.permissions?.map((permission) => (
                      <Badge
                        key={permission}
                        variant="outline"
                        className="bg-primary/5 text-primary border-primary/20 font-medium text-[11px] px-2 py-0.5"
                      >
                        {permission.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                    )) || <p className="text-sm text-muted-foreground">No permissions assigned</p>}
                  </div>
                </div>
              </div>

              {Array.isArray(users) && users.filter(user => user.role === selectedRole?.name).length > 0 && (
                <div className="space-y-3 pt-2">
                  <h4 className="text-sm font-semibold text-foreground border-b pb-1">Assigned Users</h4>
                  <div className="flex flex-wrap gap-1.5 max-h-[150px] overflow-y-auto pr-1">
                    {users.filter(user => user.role === selectedRole?.name).map((user) => (
                      <Badge
                        key={user._id}
                        variant="outline"
                        className="bg-slate-50 text-slate-700 border-slate-200 font-medium text-[11px] px-2 py-0.5"
                      >
                        {user.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="mt-6 border-t pt-4">
                <Button variant="outline" onClick={handleCloseDialog}>
                  Close
                </Button>
                {!selectedRole?.isSystem && (
                  <Button
                    onClick={() => {
                      setDialogType('edit');
                    }}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Edit Role
                  </Button>
                )}
              </DialogFooter>
            </div>
          ) : dialogType === 'delete' ? (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-foreground">
                Are you sure you want to delete the role <span className="font-semibold">"{selectedRole?.displayName}"</span>?
              </p>
              <p className="text-xs text-muted-foreground">
                This action cannot be undone. All users with this role will lose their permissions.
              </p>
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={handleCloseDialog} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  Delete Role
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <Formik
              initialValues={getInitialFormValues()}
              validationSchema={roleValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
              key={selectedRole?._id || 'new'}
            >
              {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
                const hasErrors = Object.keys(errors).length > 0 && Object.keys(touched).length > 0;
                
                const handlePermissionChange = (permission) => {
                  const newPermissions = values.permissions.includes(permission)
                    ? values.permissions.filter(p => p !== permission)
                    : [...values.permissions, permission];
                  setFieldValue('permissions', newPermissions);
                };

                return (
                  <Form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="name">Role Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="e.g., CUSTOM_ADMIN"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={dialogType === 'edit'}
                          className={touched.name && errors.name ? "border-destructive focus-visible:ring-destructive" : ""}
                        />
                        {touched.name && errors.name && (
                          <p className="text-xs text-destructive">{errors.name}</p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="displayName">Display Name *</Label>
                        <Input
                          id="displayName"
                          name="displayName"
                          placeholder="e.g., Custom Administrator"
                          value={values.displayName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={touched.displayName && errors.displayName ? "border-destructive focus-visible:ring-destructive" : ""}
                        />
                        {touched.displayName && errors.displayName && (
                          <p className="text-xs text-destructive">{errors.displayName}</p>
                        )}
                      </div>

                      <div className="space-y-1.5 col-span-1 md:col-span-2">
                        <Label htmlFor="roleType">Role Type / Scope *</Label>
                        <Select
                          disabled={dialogType === 'edit'}
                          value={values.roleType}
                          onValueChange={(val) => setFieldValue('roleType', val)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select Role Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ADMIN">Admin Platform (for Admin Portal Users)</SelectItem>
                            <SelectItem value="CLIENT">Client / Agency Portal (for Patient/Caregiver/Agency Users)</SelectItem>
                          </SelectContent>
                        </Select>
                        {touched.roleType && errors.roleType && (
                          <p className="text-xs text-destructive">{errors.roleType}</p>
                        )}
                      </div>

                      <div className="md:col-span-2 space-y-1.5">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          name="description"
                          placeholder="Describe the role's purpose and responsibilities"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          rows={2}
                          className={touched.description && errors.description ? "border-destructive focus-visible:ring-destructive" : ""}
                        />
                        {touched.description && errors.description && (
                          <p className="text-xs text-destructive">{errors.description}</p>
                        )}
                      </div>

                      <div className="md:col-span-2 space-y-2.5">
                        <Label className="text-sm font-semibold">Permissions *</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 border border-slate-150 p-4 rounded-lg bg-slate-50/50 max-h-[300px] overflow-y-auto">
                          {permissionMatrix.permissions.map((permission) => (
                            <div key={permission} className="flex items-center space-x-2">
                              <Checkbox
                                id={`perm-${permission}`}
                                checked={values.permissions.includes(permission)}
                                onCheckedChange={() => handlePermissionChange(permission)}
                              />
                              <Label
                                htmlFor={`perm-${permission}`}
                                className="text-xs font-normal text-slate-700 cursor-pointer select-none leading-none"
                              >
                                {permission.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                              </Label>
                            </div>
                          ))}
                        </div>
                        {touched.permissions && errors.permissions && (
                          <p className="text-xs text-destructive font-medium">{errors.permissions}</p>
                        )}
                      </div>
                    </div>

                    <DialogFooter className="mt-6 border-t pt-4">
                      <Button type="button" variant="outline" onClick={handleCloseDialog} disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || hasErrors}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2"
                      >
                        {isSubmitting && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        )}
                        {dialogType === 'create' ? 'Create Role' : 'Update Role'}
                      </Button>
                    </DialogFooter>
                  </Form>
                );
              }}
            </Formik>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagement;